import { Node, mergeAttributes } from "@tiptap/core";

export interface LibraryImageAttributes {
  src: string | null; // fileUuid
  alt: string | null;
  title: string | null;
  width: string | null;
  height: string | null;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    libraryImage: {
      setLibraryImage: (options: Partial<LibraryImageAttributes>) => ReturnType;
    };
  }
}

export const LibraryImageNode = Node.create({
  name: "libraryImage",
  group: "block",
  inline: false,
  draggable: true,
  selectable: true,
  atom: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-library-file-uuid"),
        renderHTML: (attributes) => ({
          "data-library-file-uuid": attributes.src,
        }),
      },
      alt: {
        default: null,
        parseHTML: (element) => element.getAttribute("alt"),
        renderHTML: (attributes) => ({ alt: attributes.alt }),
      },
      title: {
        default: null,
        parseHTML: (element) => element.getAttribute("title"),
        renderHTML: (attributes) => ({ title: attributes.title }),
      },
      width: {
        default: null,
        parseHTML: (element) => element.getAttribute("width"),
        renderHTML: (attributes) => ({ width: attributes.width }),
      },
      height: {
        default: null,
        parseHTML: (element) => element.getAttribute("height"),
        renderHTML: (attributes) => ({ height: attributes.height }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "img[data-library-file-uuid]" }];
  },

  renderHTML({ HTMLAttributes }) {
    const libraryFileUuid = HTMLAttributes["data-library-file-uuid"];

    return [
      "img",
      mergeAttributes(HTMLAttributes, {
        src: `data:image/svg+xml;base64,${btoa(`
          <svg width="300" height="200" xmlns="http://www.w3.org/2000/svg">
            <rect width="100%" height="100%" fill="#f8f9fa" stroke="#dee2e6" stroke-width="1"/>
            <text x="50%" y="50%" text-anchor="middle" dy=".3em" font-family="system-ui" font-size="12" fill="#6c757d">
              Image (${libraryFileUuid?.substring(0, 8) ?? ""}...)
            </text>
          </svg>
        `)}`,
        "data-library-file-uuid": libraryFileUuid,
        alt: HTMLAttributes.alt || "Image",
      }),
    ];
  },

  addCommands() {
    return {
      setLibraryImage:
        (options: Partial<LibraryImageAttributes>) =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: this.name,
              attrs: options,
            })
            .run();
        },
    };
  },
});
